import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';

const ROLE_LABELS = {
  super_admin: 'Super Administrateur',
  admin: 'Administrateur',
  prepose: 'Préposé Achats',
  responsable_preposes: 'Resp. Préposés',
  service_achats: 'Service Achats',
  responsable_achats: 'Resp. Achats',
  verificateur: 'Vérificateur',
  controle_gestion: 'Contrôle Gestion',
  comptable: 'Comptable',
  comptabilite: 'Comptabilité',
  caissier: 'Caissier',
  caissier_principal: 'Caissier Principal',
  direction: 'Direction',
  maintenance: 'Maintenance',
};

export default function Sidebar({ dossierCount }) {
  const { utilisateur, logout } = useAuth();
  const { peut } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const monRole = utilisateur?.role || '';

  const initiales = utilisateur
    ? ((utilisateur.prenom || '').charAt(0) + (utilisateur.nom || '').charAt(0)).toUpperCase()
    : '?';

  const nomComplet = utilisateur
    ? `${utilisateur.prenom || ''} ${utilisateur.nom || ''}`.trim()
    : '';

  const roleLabel = ROLE_LABELS[monRole] || monRole;

  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(path);
  };

  const sectionLabel = (text) => (
    <div style={{
      fontSize: 10,
      textTransform: 'uppercase',
      letterSpacing: '1.5px',
      color: 'rgba(255,255,255,0.3)',
      padding: '14px 24px 4px',
      fontWeight: 700,
    }}>{text}</div>
  );

  const navItem = (path, icon, label, badge) => {
    const active = isActive(path);
    return (
      <div
        key={path}
        onClick={() => navigate(path)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '9px 16px',
          margin: '1px 8px',
          borderRadius: 8,
          cursor: 'pointer',
          fontSize: 13,
          transition: 'background 0.15s ease, color 0.15s ease',
          background: active ? 'rgba(255,255,255,0.15)' : 'transparent',
          borderLeft: active ? '3px solid #66BB6A' : '3px solid transparent',
          color: active ? '#ffffff' : 'rgba(255,255,255,0.6)',
          fontWeight: active ? 600 : 400,
          userSelect: 'none',
        }}
        onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
        onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 14, width: 18, textAlign: 'center', display: 'inline-block' }}>{icon}</span>
          <span>{label}</span>
        </span>
        {badge != null && badge > 0 && (
          <span style={{
            background: 'rgba(255,255,255,0.2)',
            color: '#fff',
            fontSize: 10,
            padding: '1px 7px',
            borderRadius: 10,
            fontWeight: 700,
            minWidth: 18,
            textAlign: 'center',
          }}>{badge}</span>
        )}
      </div>
    );
  };

  const estAdmin = ['admin', 'super_admin'].includes(monRole);

  return (
    <div style={{
      width: 220,
      minHeight: '100vh',
      height: '100vh',
      background: '#1B5E20',
      display: 'flex',
      flexDirection: 'column',
      position: 'sticky',
      top: 0,
      overflowY: 'auto',
      flexShrink: 0,
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      scrollbarWidth: 'none',
    }}>
      {/* ── Logo ─────────────────────────────────────────── */}
      <div style={{
        padding: '12px 14px',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <img
          src="/logo-sogas.png"
          alt="SOGAS"
          style={{
            width: '100%',
            maxWidth: '170px',
            height: 'auto',
            objectFit: 'contain'
          }}
        />
      </div>

      {/* ── Utilisateur ──────────────────────────────────── */}
      <div style={{
        padding: '12px 16px',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 34,
            height: 34,
            borderRadius: '50%',
            flexShrink: 0,
            background: 'rgba(255,255,255,0.15)',
            border: '1.5px solid rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontWeight: 700,
            fontSize: 12,
          }}>{initiales}</div>
          <div style={{ overflow: 'hidden', flex: 1 }}>
            <div style={{
              color: '#fff',
              fontSize: 13,
              fontWeight: 600,
              lineHeight: 1.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>{nomComplet || 'Utilisateur'}</div>
            <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11, lineHeight: 1.3 }}>
              {roleLabel}
            </div>
          </div>
        </div>
      </div>

      {/* ── Navigation ───────────────────────────────────── */}
      <div style={{ flex: 1, padding: '6px 0' }}>
        {sectionLabel('Principal')}
        {navItem('/dashboard', '⊞', 'Tableau de bord')}

        {sectionLabel('Modules')}
        {peut('abattage',    'lire') && navItem('/saisies',          '🐄', 'Production')}
        {peut('facturation', 'lire') && navItem('/facturation/liste','🧾', 'Facturation')}
        {peut('caisse',      'lire') && navItem('/caisses',          '💳', 'Caisse')}
        {peut('achats',      'lire') && navItem('/achats',           '🛒', 'Achats', dossierCount)}
        {peut('rapports',    'lire') && navItem('/rapports',         '📊', 'Rapports')}

        {estAdmin && (
          <>
            {sectionLabel('Admin')}
            {navItem('/users',       '👥', 'Utilisateurs')}
            {navItem('/roles',       '🔐', 'Rôles & Accès')}
            {navItem('/parametres',  '⚙️', 'Paramètres')}
          </>
        )}
      </div>

      {/* ── Déconnexion + footer ─────────────────────────── */}
      <div style={{ padding: '8px 8px 12px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <div
          onClick={() => { logout(); navigate('/login'); }}
          style={{
            padding: '8px 16px',
            borderRadius: 8,
            cursor: 'pointer',
            fontSize: 13,
            color: 'rgba(255,255,255,0.5)',
            transition: '0.15s ease',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = '#fff'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; }}
        >
          <span>🚪</span><span>Déconnexion</span>
        </div>
        <div style={{
          textAlign: 'center',
          color: 'rgba(255,255,255,0.2)',
          fontSize: 10,
          padding: '6px 0 2px',
          letterSpacing: '0.5px',
        }}>SOGAS V2 · 2026</div>
      </div>
    </div>
  );
}
